import { z } from 'zod'
import {
  daySettingsSchema,
  daySettingsObject,
  dayTradeModeSchema,
  stripDefault,
  type DaySettingsDto,
} from './backtest-config.schema'
import { dayTradeSessionStateSchema } from './day-trade-session-state.schema'
import { DayTradeBotEventType } from '../../enums/day-trade-bot-event-type.enum'

// per-instrument override: any subset of the strategy settings, merged over the
// account defaults to form the effective config the engine runs.
//
// a plain `daySettingsSchema.partial()` is wrong here: Zod's `.partial()` keeps the inner
// `.default()` wrappers, so parsing a sparse override (e.g. `{ dropPct: 1.5 }`) re-injects
// every defaulted field (budget, factorWeights, *Max band, sellFromAverage, …) as an explicit
// override. that snaps those knobs to the schema default instead of inheriting the account
// default. `stripDefault` (shared with the backtest override) strips the defaults first so an
// omitted key truly stays omitted (= inherit).
// budget is a single account-level shared pool (see daySettingsSchema), so it is intentionally
// NOT overridable per instrument — dropping it from the override shape strips it from any stored
// override on parse, and mergeDaySettings ignores a legacy stored budget for the same reason.
const dayTradeSettingsOverrideShape = Object.fromEntries(
  Object.entries(daySettingsObject.shape)
    .filter(([key]) => key !== 'budget')
    .map(([key, field]) => [key, stripDefault(field as unknown as z.ZodTypeAny)])
) as z.ZodRawShape
// the dynamic shape erases the per-field static types, so re-assert the precise override type
// (a true partial of the day settings, minus budget) — runtime still strips defaults via the
// shape above
export const dayTradeSettingsOverrideSchema = z
  .object(dayTradeSettingsOverrideShape)
  .partial() as unknown as z.ZodType<Partial<Omit<DaySettingsDto, 'budget'>>>
export type DayTradeSettingsOverrideDto = Partial<Omit<DaySettingsDto, 'budget'>>

// a 24-hour "HH:mm" wall clock. the daily-report time is stored in UTC (the frontend picks a
// local time and converts), matching RecurrentIncome.notifyTime.
export const DAILY_REPORT_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/
export const dailyReportTimeSchema = z
  .string()
  .regex(DAILY_REPORT_TIME_PATTERN, 'must be a UTC time in HH:mm format')

// account-level config: enable flag + the default strategy settings every instrument
// inherits unless it overrides them
export const dayTradeBotSettingsSchema = z.object({
  id: z.string().guid(),
  brokerAccountId: z.string().guid(),
  isEnabled: z.boolean().default(false),
  // account-level sell-only master switch: when true, every instrument under the account runs
  // in wind-down mode (buy leg blocked, sell leg unchanged) regardless of its own flag
  sellOnly: z.boolean().default(false),
  defaults: daySettingsSchema,
  // daily Telegram report: opt-in, delivered at dailyReportTime (UTC HH:mm). dailyReportOnEmpty
  // sends it even on a day with no executed trades; otherwise such a day stays silent.
  dailyReportEnabled: z.boolean().default(false),
  dailyReportTime: dailyReportTimeSchema.nullable().default(null),
  dailyReportOnEmpty: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})
export type DayTradeBotSettingsDto = z.infer<typeof dayTradeBotSettingsSchema>

// enable/disable are separate endpoints, so this update path never honored isEnabled — the field
// was dead (PUT /settings { isEnabled } returned 200 and did nothing). dropped (#240).
export const updateDayTradeBotSettingsSchema = z.object({
  sellOnly: z.boolean().optional(),
  defaults: daySettingsSchema.optional(),
  dailyReportEnabled: z.boolean().optional(),
  dailyReportTime: dailyReportTimeSchema.nullable().optional(),
  dailyReportOnEmpty: z.boolean().optional(),
})
export type UpdateDayTradeBotSettingsDto = z.infer<typeof updateDayTradeBotSettingsSchema>

// result of a one-off sample daily report. `delivered` is false when the user has no active
// Telegram connection — a hint to go connect one, not an error.
export const dayTradeDailyReportTestResponseSchema = z.object({
  delivered: z.boolean(),
})
export type DayTradeDailyReportTestResponseDto = z.infer<
  typeof dayTradeDailyReportTestResponseSchema
>

export const createDayTradeBotInstrumentSchema = z.object({
  instrumentUid: z.string().min(1),
  figi: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  instrumentName: z.string().nullable().optional(),
  isEnabled: z.boolean().default(true),
  // one-lot notional override fed into the dip pipeline; null = compute lotSize*price live
  lotPrice: z.number().positive().nullable().optional(),
  // when set, the named backtest preset's `defaults` become this instrument's settings override
  presetId: z.string().optional(),
  // inline overrides; omitted fields fall back to the account defaults
  settings: dayTradeSettingsOverrideSchema.nullable().optional(),
})
export type CreateDayTradeBotInstrumentDto = z.infer<typeof createDayTradeBotInstrumentSchema>

export const updateDayTradeBotInstrumentSchema = z.object({
  isEnabled: z.boolean().optional(),
  sellOnly: z.boolean().optional(),
  lotPrice: z.number().positive().nullable().optional(),
  presetId: z.string().optional(),
  settings: dayTradeSettingsOverrideSchema.nullable().optional(),
})
export type UpdateDayTradeBotInstrumentDto = z.infer<typeof updateDayTradeBotInstrumentSchema>

export const dayTradeBotInstrumentResponseSchema = z.object({
  id: z.string().guid(),
  brokerAccountId: z.string().guid(),
  instrumentUid: z.string(),
  figi: z.string().nullable(),
  ticker: z.string().nullable(),
  instrumentName: z.string().nullable(),
  isEnabled: z.boolean(),
  // this instrument's own sell-only switch
  sellOnly: z.boolean(),
  // the gate the engine actually applies: this instrument's own flag OR the account-level one.
  // when the account master switch is on, this is true even if the row's own flag is off.
  effectiveSellOnly: z.boolean(),
  lotPrice: z.number().nullable(),
  settings: dayTradeSettingsOverrideSchema.nullable(),
  // set when the start-time guard auto-disabled this instrument on boot because the account held
  // lots bought outside the bot: the held quantity (in lots) + broker average captured then, so the
  // page can offer to adopt them. both null once resolved (adopted or dismissed).
  heldOutsideLots: z.number().int().nullable(),
  heldOutsideAvgPrice: z.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // account defaults merged with this instrument's overrides — the config the engine runs
  effectiveSettings: daySettingsSchema,
})
export type DayTradeBotInstrumentResponseDto = z.infer<typeof dayTradeBotInstrumentResponseSchema>

// live session state: the base engine display state plus the lot size, one-lot notional,
// realized-today P&L, and the buy/sell arm distances the live tab surfaces in the table
export const dayTradeBotSessionStateSchema = dayTradeSessionStateSchema.extend({
  lotSize: z.number().int().nullable(),
  lotPrice: z.number().nullable(),
  realizedToday: z.number(),
  // cumulative realized net P&L over the session's life (survives daily resets + restarts)
  realizedAllTime: z.number(),
  // cumulative cost basis of closed round-trips — the denominator for the all-time return %
  realizedAllTimeBasis: z.number(),
  // price at/below which a buy arms (null when the buy leg is inactive — cap reached / not averaging down)
  buyArmPrice: z.number().nullable(),
  // price at/above which a sell arms (null when flat — nothing to sell)
  sellArmPrice: z.number().nullable(),
  // percent the price must FALL to arm a buy (≤ 0 means already at/below the arm)
  pctUntilBuyArm: z.number().nullable(),
  // percent the price must RISE to arm a sell (≤ 0 means already at/above the arm)
  pctUntilSellArm: z.number().nullable(),
  // the effective step % that sets the buy arm (drop-from-peak, or shift while averaging down)
  buyArmPct: z.number().nullable(),
  // the effective step % that sets the sell arm (rise above average, or sell-shift chain)
  sellArmPct: z.number().nullable(),
  // strategy mode the session is running (badge + conditional UI)
  mode: dayTradeModeSchema,
  // sell-only wind-down is active for this session (buy leg blocked; sell leg unchanged). true
  // when the account master switch OR this instrument's own switch is on.
  sellOnly: z.boolean(),
  // 30-day average daily range % driving the min-spread ladder (null in manual mode / before R is set)
  volatilityRangePct: z.number().nullable(),
  // min-spread reachability: false warns the floor rarely fills for this instrument's volatility
  // (null in manual mode / before R is set)
  minSpreadReachable: z.boolean().nullable(),
  // 30-day price band (min low / max high over the last 30 daily candles) — drives the ladder bar's
  // range highlight in all modes; null until the band is known (candle-less run / fetch failure)
  thirtyDayLow: z.number().nullable(),
  thirtyDayHigh: z.number().nullable(),
  // realized-gain tax rate as a FRACTION (e.g. 0.13) — feeds the min-spread sell floor so the
  // frontend ladder bar recomputes the exact same rungs the engine arms
  taxRate: z.number(),
})
export type DayTradeBotSessionStateDto = z.infer<typeof dayTradeBotSessionStateSchema>

export const dayTradeBotEventSchema = z.object({
  id: z.string(),
  timestamp: z.coerce.date(),
  eventType: z.nativeEnum(DayTradeBotEventType),
  // ticker of the instrument the event concerns; the activity feed is account-level and spans
  // every instrument, so each row carries its own ticker. null when the instrument is unknown.
  ticker: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
})
export type DayTradeBotEventDto = z.infer<typeof dayTradeBotEventSchema>

export const dayTradeBotEventsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
})
export type DayTradeBotEventsQueryDto = z.infer<typeof dayTradeBotEventsQuerySchema>
